import { ComponentPropsWithoutRef, ElementType, useState } from 'react';

import clsx from 'clsx';

import useTranslation from '@/utils/hooks/translation/useTranslation';
// Utils
import useIsGroup from '@/utils/hooks/user/useIsGroup';

import Icon from '@/components/base/Icon';
// Components
import Tooltip from '@/components/base/Tooltip';

type ButtonCopyLinkProps<TAs extends ElementType = 'button'> = {
    link: string;
    displayIcon?: boolean;
    isGroup?: ReturnType<typeof useIsGroup>;
    as?: TAs;
};

export function ButtonCopyLink<TAs extends ElementType>(
    props: ButtonCopyLinkProps<TAs> &
        DistributiveOmit<ComponentPropsWithoutRef<ElementType extends TAs ? 'button' : TAs>, 'as'>
) {
    const { isGroup, link, displayIcon, as: Component = 'button', ...restProps } = props;
    const [copiedToClipboard, setCopiedToClipboard] = useState(false);
    const { t } = useTranslation('scheduler');

    return (
        <Tooltip
            content={t(
                clsx({
                    'collaboration.settings.copied_to_clipboard': copiedToClipboard,
                    'collaboration.settings.copy_sharing_link_group': isGroup && !copiedToClipboard,
                    'collaboration.settings.copy_sharing_link': !isGroup && !copiedToClipboard
                })
            )}
            placement="bottom"
            hideOnClick={false}>
            <Component
                onClick={() => {
                    navigator.clipboard.writeText(link);
                    setCopiedToClipboard(true);
                    setTimeout(() => {
                        setCopiedToClipboard(false);
                    }, 1000);
                }}
                {...restProps}>
                {displayIcon ? (
                    <Icon icon="collaboration" />
                ) : (
                    t(
                        clsx({
                            copy_share_link_group: isGroup,
                            copy_share_link: !isGroup
                        })
                    )
                )}
            </Component>
        </Tooltip>
    );
}
